// Requêtes des écrans des marchés et du domaine public (TanStack Query). Clés sous « marches »
// et « domaine-public » ; le changement de contexte vide le cache (FournisseurSession).
import type { PlanMarche } from '@cc/contrats';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import type {
  Attribution,
  AttributionDetail,
  Autorisation,
  CarteDomainePublic,
  CarteMarches,
  CreationAttribution,
  CreationMarche,
  EmplacementDetail,
  Liste,
  MarcheDetail,
  MarcheResume,
  ModificationEmplacement,
  ModificationEmprise,
  RenouvellementAttribution,
  ResiliationAttribution,
  TarificationEmplacement,
} from './types';

export const CLES = {
  marches: ['marches'] as const,
  marche: (id: string) => ['marches', 'fiche', id] as const,
  emplacement: (id: string) => ['marches', 'emplacement', id] as const,
  tarification: (id: string, debut: string, fin: string) => ['marches', 'tarification', id, debut, fin] as const,
  attributions: (filtres: Record<string, string>) => ['marches', 'attributions', filtres] as const,
  attribution: (id: string) => ['marches', 'attribution', id] as const,
  autorisations: (filtres: Record<string, string>) => ['domaine-public', 'autorisations', filtres] as const,
  autorisation: (id: string) => ['domaine-public', 'autorisation', id] as const,
  carteMarches: (bbox: string) => ['marches', 'carte', bbox] as const,
  carteDomainePublic: (bbox: string, enCours: string) => ['domaine-public', 'carte', bbox, enCours] as const,
};

export const INVALIDER_MARCHES: readonly (readonly unknown[])[] = [['marches'], ['indicateurs']];
export const INVALIDER_DOMAINE_PUBLIC: readonly (readonly unknown[])[] = [['domaine-public'], ['indicateurs']];

export function useMarches(filtres: { actif?: string } = {}) {
  return useQuery({
    queryKey: [...CLES.marches, filtres],
    queryFn: ({ signal }) => requete<Liste<MarcheResume>>('/marches', { signal, parametres: filtres }).then((r) => r.elements),
    retry: false,
  });
}

export function useMarche(id: string | null) {
  return useQuery({
    queryKey: CLES.marche(id ?? ''),
    queryFn: ({ signal }) => requete<MarcheDetail>(`/marches/${id}`, { signal }),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useEmplacement(id: string | null) {
  return useQuery({
    queryKey: CLES.emplacement(id ?? ''),
    queryFn: ({ signal }) => requete<EmplacementDetail>(`/marches/emplacements/${id}`, { signal }),
    enabled: Boolean(id),
    retry: false,
  });
}

/** Redevance d'une période, calculée par le module des recettes ; un refus (409, 404) est rendu tel quel. */
export function useTarification(emplacementId: string | null, periode: { debut: string; fin: string } | null) {
  return useQuery({
    queryKey: CLES.tarification(emplacementId ?? '', periode?.debut ?? '', periode?.fin ?? ''),
    queryFn: ({ signal }) => requete<TarificationEmplacement>(`/marches/emplacements/${emplacementId}/tarification`, { signal, parametres: periode ?? {} }),
    enabled: Boolean(emplacementId && periode && periode.debut && periode.fin && periode.fin >= periode.debut),
    retry: false,
    staleTime: 60_000,
  });
}

export function useAttributions(filtres: Record<string, string>, actif = true) {
  return useQuery({
    queryKey: CLES.attributions(filtres),
    queryFn: ({ signal }) => requete<Liste<Attribution>>('/marches/attributions', { signal, parametres: filtres }).then((r) => r.elements),
    enabled: actif,
    retry: false,
  });
}

export function useAttribution(id: string | null) {
  return useQuery({
    queryKey: CLES.attribution(id ?? ''),
    queryFn: ({ signal }) => requete<AttributionDetail>(`/marches/attributions/${id}`, { signal }),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useAutorisations(filtres: Record<string, string>) {
  return useQuery({
    queryKey: CLES.autorisations(filtres),
    queryFn: ({ signal }) => requete<Liste<Autorisation>>('/domaine-public/autorisations', { signal, parametres: filtres }).then((r) => r.elements),
    retry: false,
  });
}

export function useAutorisation(id: string | null) {
  return useQuery({
    queryKey: CLES.autorisation(id ?? ''),
    queryFn: ({ signal }) => requete<Autorisation>(`/domaine-public/autorisations/${id}`, { signal }),
    enabled: Boolean(id),
    retry: false,
  });
}

/** Couche des marchés (points) filtrée par l'emprise affichée (ouest,sud,est,nord). */
export function useCarteMarches(bbox: string | null, actif = true) {
  return useQuery({
    queryKey: CLES.carteMarches(bbox ?? ''),
    queryFn: ({ signal }) => requete<CarteMarches>('/carte/marches', { signal, parametres: { bbox } }),
    enabled: actif && Boolean(bbox),
    retry: false,
  });
}

/** Couche des autorisations (emprises ou positions) filtrée par l'emprise affichée. */
export function useCarteDomainePublic(bbox: string | null, enCours: boolean, actif = true) {
  return useQuery({
    queryKey: CLES.carteDomainePublic(bbox ?? '', enCours ? 'oui' : ''),
    queryFn: ({ signal }) => requete<CarteDomainePublic>('/carte/domaine-public', { signal, parametres: { bbox, en_cours: enCours ? 'oui' : undefined } }),
    enabled: actif && Boolean(bbox),
    retry: false,
  });
}

// Écritures ---------------------------------------------------------------------------------

export const creerMarche = (corps: CreationMarche) => requete<MarcheResume>('/marches', { methode: 'POST', corps });
export const modifierPlan = (marcheId: string, plan: PlanMarche, version: number) => requete<MarcheResume>(`/marches/${marcheId}/plan`, { methode: 'PUT', corps: { plan, version } });
export const modifierEmplacement = (id: string, corps: ModificationEmplacement) => requete<EmplacementDetail>(`/marches/emplacements/${id}`, { methode: 'PATCH', corps });
export const creerAttribution = (corps: CreationAttribution) => requete<AttributionDetail>('/marches/attributions', { methode: 'POST', corps });
export const resilierAttribution = (id: string, corps: ResiliationAttribution) => requete<AttributionDetail>(`/marches/attributions/${id}/resilier`, { methode: 'POST', corps });
export const renouvelerAttribution = (id: string, corps: RenouvellementAttribution) => requete<AttributionDetail>(`/marches/attributions/${id}/renouveler`, { methode: 'POST', corps });
export const modifierEmprise = (id: string, corps: ModificationEmprise) => requete<Autorisation>(`/domaine-public/autorisations/${id}/emprise`, { methode: 'PUT', corps });
export const retirerAutorisation = (id: string, motif: string) => requete<Autorisation>(`/domaine-public/autorisations/${id}/retirer`, { methode: 'POST', corps: { motif } });
